import { createPortal } from "react-dom";
import { useLayoutEffect, useRef, useState } from "react";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import ThemedEmojiPicker from "./ThemedEmojiPicker";
import { getPortalLayerTarget } from "./Portal/portalRoot";

const VIEWPORT_PAD = 12;
const PICKER_OFFSET = 8;
const FALLBACK_WIDTH = 350;
const FALLBACK_HEIGHT = 400;

/**
 * Renders the emoji picker on the portal root so feed Follow buttons
 * and card frost cannot paint through it. Anchors above the emoji icon.
 */
export default function PortalEmojiPicker({
  show,
  anchorRef,
  onClose,
  onEmojiClick,
  className = "",
  ...pickerProps
}) {
  const panelRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!show || !anchorRef?.current) {
      setCoords(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const rect = anchor.getBoundingClientRect();
      const panel = panelRef.current;
      const pickerWidth = panel?.offsetWidth || FALLBACK_WIDTH;
      const pickerHeight = panel?.offsetHeight || FALLBACK_HEIGHT;
      const isRtl = document.documentElement.getAttribute("dir") === "rtl";

      let top = rect.top - pickerHeight - PICKER_OFFSET;
      if (top < VIEWPORT_PAD) {
        top = Math.min(
          rect.bottom + PICKER_OFFSET,
          window.innerHeight - pickerHeight - VIEWPORT_PAD
        );
      }
      top = Math.max(
        VIEWPORT_PAD,
        Math.min(top, window.innerHeight - pickerHeight - VIEWPORT_PAD)
      );

      let left = isRtl ? rect.left : rect.right - pickerWidth;
      left = Math.min(
        Math.max(VIEWPORT_PAD, left),
        window.innerWidth - pickerWidth - VIEWPORT_PAD
      );

      setCoords({ top, left });
    };

    updatePosition();
    const raf = requestAnimationFrame(updatePosition);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [show, anchorRef]);

  useLayoutEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose?.();
      }
    };

    const onPointerDown = (event) => {
      const panel = panelRef.current;
      const anchor = anchorRef?.current;
      if (
        panel &&
        !panel.contains(event.target) &&
        anchor &&
        !anchor.contains(event.target)
      ) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("mousedown", onPointerDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("mousedown", onPointerDown, true);
    };
  }, [show, onClose, anchorRef]);

  if (!show || !coords) {
    return null;
  }

  const target = getPortalLayerTarget("picker") || document.body;

  return createPortal(
    <div
      ref={panelRef}
      className={`HuzEmojiPickerChangeColor wlj-portal-emoji-picker ${className}`.trim()}
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        right: "auto",
        bottom: "auto",
        zIndex: "var(--wlj-z-picker, 1350)",
      }}
      role="dialog"
      aria-label="Emoji picker"
    >
      <ThemedEmojiPicker {...pickerProps} onEmojiClick={onEmojiClick} />
      <button
        type="button"
        className="emoji-close"
        onClick={onClose}
        aria-label="Close emoji picker"
      >
        <CloseSharpIcon className="material-symbols-sharp" />
      </button>
    </div>,
    target
  );
}
